import React, { useContext } from "react";
import { ModalContext } from "../../context/ModalContext";
import { quitarClaveNombre } from "../../utils/destajos";
import { ProyectosContext } from "../../context/ProyectosContext";
import { formatMonto, porcentajeDe } from "../../utils";

export const INFO_AVANCE_FINANCIERO = (
  <>
    <div>
      "Según avance" relaciona el avance físico reportado en el cronograma
      contra el importe de cada concepto del contrato.
    </div>
    <div className="mt-1">
      "Pagado" suma los pagos no cancelados, incluido el anticipo. Se calcula
      cada vez que se abre esta vista.
    </div>
  </>
);

const Dato = ({ label, children, detalle, className = "", onInfo }) => (
  <div className="col-6 col-md-4 mb-3">
    <div className="small text-muted d-flex align-items-center">
      {label}
      {onInfo && (
        <button
          type="button"
          className="btn btn-sm btn-outline-primary rounded-circle ms-2 p-0 flex-shrink-0 no-print"
          style={{ width: 20, height: 20, fontSize: 10 }}
          title="Ver conceptos"
          onClick={onInfo}
        >
          <i className="fa fa-info" />
        </button>
      )}
    </div>
    <div className={`bold h5 mb-0 ${className}`}>{children}</div>
    {detalle && <div className="small text-muted">{detalle}</div>}
  </div>
);

// Earned value from the cronograma (conceptos × avance of their destajo)
// against what has been authorized and paid, per contrato.
// List of the contrato conceptos that are not in the cronograma.
const ConceptosFueraCronograma = ({ conceptos }) => (
  <div style={{ maxHeight: "65vh", overflowY: "auto" }}>
    {conceptos.map((concepto) => (
      <div
        key={concepto.idConcepto}
        className="d-flex align-items-start border-bottom py-2"
      >
        <div className="flex-grow-1" style={{ minWidth: 0 }}>
          <div className="small">
            {concepto.clave && (
              <span className="text-muted me-1">[{concepto.clave}]</span>
            )}
            {quitarClaveNombre(concepto.concepto, concepto.clave)}
          </div>
          <div className="small text-muted">
            {concepto.contratista || "Sin contratista"}
            {concepto.folio && <> · {concepto.folio}</>}
            {concepto.cantidad > 0 && (
              <>
                {" · "}
                {concepto.cantidad} {concepto.unidad || ""}
              </>
            )}
          </div>
        </div>
        <div className="bold small text-nowrap ms-3">
          ${formatMonto(concepto.importe)}
        </div>
      </div>
    ))}
  </div>
);

// `datos`: an already calculated avance (reports); otherwise it is fetched.
// datos: avance saved with a report; otherwise the one in context.
const AvanceFinanciero = ({ datos }) => {
  const { modalComponent } = useContext(ModalContext);
  const { avanceFinanciero } = useContext(ProyectosContext);
  const avance = datos !== undefined ? datos : avanceFinanciero;

  if (avance === undefined) {
    return <div className="spinner-border spinner-border-sm m-3" />;
  }
  if (!avance || avance.contratos.length === 0) {
    return (
      <p className="text-muted px-3 mb-0">
        La obra no tiene contratos para calcular su avance financiero.
      </p>
    );
  }

  const { totales, contratos } = avance;
  const porPagar = totales.por_pagar;
  // Contratista that has gone the longest without estimating.
  const masSinEstimar = contratos
    .filter((contrato) => contrato.dias_sin_estimar !== null)
    .sort((a, b) => b.dias_sin_estimar - a.dias_sin_estimar)[0];
  const conceptosFuera = avance.conceptos_fuera_cronograma || [];

  const handleConceptosFuera = () =>
    modalComponent(
      "Conceptos no presentes en cronograma",
      <ConceptosFueraCronograma conceptos={conceptosFuera} />
    );

  // Importe of the contrato conceptos that are not in the cronograma (left out
  // of "según avance"), with the list in a modal.
  const renderConceptosFuera = () => {
    if (!(totales.importe_sin_destajo > 0)) return null;
    const hayConceptos = conceptosFuera.length > 0;
    return (
      <Dato
        label="Conceptos no presentes en cronograma"
        detalle={`${conceptosFuera.length} no incluidos en el cálculo`}
        onInfo={hayConceptos && handleConceptosFuera}
        className="text-muted"
      >
        ${formatMonto(totales.importe_sin_destajo)}
      </Dato>
    );
  };

  return (
    <div className="container-fluid px-3">
      <div className="row">
        <Dato label="Total de contratos">
          ${formatMonto(totales.total_contrato)}
        </Dato>
        <Dato
          label="Debería estar pagado (según avance)"
          detalle={`${porcentajeDe(totales.devengado, totales.total_contrato)} del total`}
          className="text-primary"
        >
          ${formatMonto(totales.devengado)}
        </Dato>
        <Dato
          label="Pagado"
          detalle={`${porcentajeDe(totales.pagado, totales.total_contrato)} del total${
            totales.anticipo > 0
              ? ` · incluye $${formatMonto(totales.anticipo)} de anticipo`
              : ""
          }`}
        >
          ${formatMonto(totales.pagado)}
        </Dato>
        <Dato
          label="Estimado autorizado"
          detalle={`${porcentajeDe(totales.autorizado, totales.total_contrato)} del total`}
        >
          ${formatMonto(totales.autorizado)}
        </Dato>
        <Dato
          label={porPagar >= 0 ? "Pendiente por pagar" : "Pagado de más"}
          className={porPagar >= 0 ? "text-danger" : "text-success"}
          detalle="Debería estar pagado − pagado"
        >
          ${formatMonto(Math.abs(porPagar))}
        </Dato>
        {masSinEstimar && (
          <Dato
            label="Días sin estimar"
            detalle={`${masSinEstimar.contratista || "Sin contratista"}${
              masSinEstimar.num_estimaciones === 0 ? " · nunca ha estimado" : ""
            }`}
            className={
              masSinEstimar.dias_sin_estimar > 14
                ? "text-danger"
                : masSinEstimar.dias_sin_estimar > 7
                ? "text-warning"
                : "text-success"
            }
          >
            {masSinEstimar.dias_sin_estimar}{" "}
            {masSinEstimar.dias_sin_estimar === 1 ? "día" : "días"}
          </Dato>
        )}
        {renderConceptosFuera()}
      </div>
    </div>
  );
};

export default AvanceFinanciero;
